import mergeDeepLeft from 'ramda/src/mergeDeepLeft';
import TokenHandler from '~/modules/TokenHandler';
import defaultArticleConfig from './default.config';
import type { IArticleConfig } from './types';
import { getTextFromHTML } from '~/utils/dom';
import { makeDevLog } from '@vuukle/utils/dist/es';

export type { IArticleConfig } from './types';

/** Save publisher config for easier access */
const publisherConfig = window.VUUKLE_CONFIG || window._config_setup;

/** ❓ Check if publisher have VUUKLE_CONFIG available */
// On the 2nd comparison we need to make sure that the 'object' type config is not an array or null since both of them are typeof 'object'
if (
  !(publisherConfig instanceof Object) &&
  (typeof publisherConfig !== 'object' || Array.isArray(publisherConfig) || publisherConfig === null)
) {
  throw new Error(`⚠️[VUUKLE] VUUKLE_CONFIG can't be found or it's not valid`);
}
/** Regex to test if API key is GUIDv4 compliant */
/** Usage retracted as it's not a viable solution to fake apikeys */
// export const GUIDv4Regex: RegExp = /^[0-9A-F]{8}-[0-9A-F]{4}-[14][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i;

/** 🌐 Validate Article URL */
try {
  new URL(publisherConfig.url); // tslint:disable-line
} catch (_err) {
  // NOTE: we can't make variable just undefined since mergeDeep won't replace undefined with needed value
  delete publisherConfig.url;
}

/** 🔧 Validate article title */
if (publisherConfig.title) {
  publisherConfig.title = getTextFromHTML(publisherConfig.title);
}

/** 🔧 Validate host param */
if (typeof publisherConfig.host === 'string') {
  publisherConfig.host = publisherConfig.host
    .replace(/https?\:\/\//g, '')
    .replace(/^www\./, '')
    .replace(/^(m\.)/, '');
}

/** 🔧 Fill link param with protocol */
if (typeof publisherConfig.link === 'string') {
  publisherConfig.link = publisherConfig.link.replace('[protocol]', `${window.location.protocol}//`);
}

/** 🔧 Validate API Key. If not provided, prevent widgets from initializing */
if (!publisherConfig.apiKey) {
  throw new Error('⚠ [VUUKLE] API key is missing in the VUUKLE_CONFIG');
}

/** When articleId is not provided or it's equal to 1 or '1', default to the article URL link */
if (!publisherConfig.articleId || publisherConfig.articleId === 1 || publisherConfig.articleId === '1') {
  publisherConfig.articleId = publisherConfig.url || defaultArticleConfig.articleId;
}

/** Set SSO mode to true if the publisher has SSO config */
if (typeof publisherConfig.comments?.auth?.sso?.onClick === 'function') {
  TokenHandler.setSSOInfo(true);
}

/** Convert an array of tags into a comma separated string of tags */
if (publisherConfig.tags && Array.isArray(publisherConfig.tags)) {
  publisherConfig.tags = publisherConfig.tags.join(',');
}

/** In case the provided tags are not a string (even after the array check), make the value blank */
if (publisherConfig.tags && typeof publisherConfig.tags !== 'string') {
  publisherConfig.tags = '';
}

try {
  const searchParams = new URLSearchParams(window.location.search);
  const skipQuizzly = searchParams.get('skipQuizzly');
  if (skipQuizzly === 'true') {
    if (publisherConfig.quiz) {
      publisherConfig.quiz.enabled = false;
    } else {
      publisherConfig.quiz = { enabled: false };
    }
  }
} catch (_e) {
  makeDevLog('log', 'Error parsing search params for disabling Quizzly');
}

/** Create final config to export */
const finalConfig: IArticleConfig = mergeDeepLeft(publisherConfig, defaultArticleConfig) as IArticleConfig;

/** 🏁 Finally merge global config with default config */
if (window.VUUKLE_CONFIG) {
  window.VUUKLE_CONFIG = finalConfig;
}
if (window._config_setup) {
  window._config_setup = finalConfig;
}

export default finalConfig;
